var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { BaseMessageSignerWalletAdapter, WalletAccountError, WalletConnectionError, WalletDisconnectionError, WalletError, WalletNotConnectedError, WalletNotReadyError, WalletPublicKeyError, WalletSignTransactionError, } from '@solana/wallet-adapter-base';
import { PublicKey } from '@solana/web3.js';
import bs58 from 'bs58';
export class SlopeWalletAdapter extends BaseMessageSignerWalletAdapter {
    constructor(config = {}) {
        super();
        this._connecting = false;
        this._wallet = null;
        this._publicKey = null;
    }
    get publicKey() {
        return this._publicKey;
    }
    get connecting() {
        return this._connecting;
    }
    ready() {
        return __awaiter(this, void 0, void 0, function* () {
            if (typeof window === 'undefined' || typeof document === 'undefined')
                return false;
            if (document.readyState === 'complete')
                return typeof window.Slope === 'function';
            return new Promise((resolve) => {
                function listener() {
                    window.removeEventListener('load', listener);
                    resolve(typeof window.Slope === 'function');
                }
                window.addEventListener('load', listener);
            });
        });
    }
    connect() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                if (this.connected || this.connecting)
                    return;
                this._connecting = true;
                if (!(yield this.ready()))
                    throw new WalletNotReadyError();
                const wallet = new window.Slope();
                let data;
                try {
                    ({ data } = yield wallet.connect());
                }
                catch (error) {
                    throw new WalletConnectionError(error === null || error === void 0 ? void 0 : error.message, error);
                }
                if (!data.publicKey)
                    throw new WalletAccountError();
                let publicKey;
                try {
                    publicKey = new PublicKey(data.publicKey);
                }
                catch (error) {
                    throw new WalletPublicKeyError(error === null || error === void 0 ? void 0 : error.message, error);
                }
                this._wallet = wallet;
                this._publicKey = publicKey;
                this.emit('connect');
            }
            catch (error) {
                this.emit('error', error);
                throw error;
            }
            finally {
                this._connecting = false;
            }
        });
    }
    disconnect() {
        return __awaiter(this, void 0, void 0, function* () {
            const wallet = this._wallet;
            if (wallet) {
                this._wallet = null;
                this._publicKey = null;
                try {
                    const { msg } = yield wallet.disconnect();
                    if (msg !== 'ok')
                        throw new WalletDisconnectionError(msg);
                }
                catch (error) {
                    if (!(error instanceof WalletError)) {
                        // eslint-disable-next-line no-ex-assign
                        error = new WalletDisconnectionError(error === null || error === void 0 ? void 0 : error.message, error);
                    }
                    this.emit('error', error);
                }
            }
            this.emit('disconnect');
        });
    }
    signTransaction(transaction) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const wallet = this._wallet;
                if (!wallet)
                    throw new WalletNotConnectedError();
                try {
                    const message = bs58.encode(transaction.serializeMessage());
                    const { msg, data } = yield wallet.signTransaction(message);
                    if (!data.publicKey || !data.signature)
                        throw new WalletSignTransactionError(msg);
                    const publicKey = new PublicKey(data.publicKey);
                    const signature = bs58.decode(data.signature);
                    transaction.addSignature(publicKey, signature);
                    return transaction;
                }
                catch (error) {
                    if (error instanceof WalletError)
                        throw error;
                    throw new WalletSignTransactionError(error === null || error === void 0 ? void 0 : error.message, error);
                }
            }
            catch (error) {
                this.emit('error', error);
                throw error;
            }
        });
    }
    signAllTransactions(transactions) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const wallet = this._wallet;
                if (!wallet)
                    throw new WalletNotConnectedError();
                try {
                    const messages = transactions.map((transaction) => bs58.encode(transaction.serializeMessage()));
                    const { msg, data } = yield wallet.signAllTransactions(messages);
                    const length = transactions.length;
                    if (!data.publicKey || ((_a = data.signatures) === null || _a === void 0 ? void 0 : _a.length) !== length)
                        throw new WalletSignTransactionError(msg);
                    const publicKey = new PublicKey(data.publicKey);
                    for (let i = 0; i < length; i++) {
                        transactions[i].addSignature(publicKey, bs58.decode(data.signatures[i]));
                    }
                    return transactions;
                }
                catch (error) {
                    if (error instanceof WalletError)
                        throw error;
                    throw new WalletSignTransactionError(error === null || error === void 0 ? void 0 : error.message, error);
                }
            }
            catch (error) {
                this.emit('error', error);
                throw error;
            }
        });
    }
    signMessage(message) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const wallet = this._wallet;
                if (!wallet)
                    throw new WalletNotConnectedError();
                try {
                    const response = yield wallet.signMessage(message);
                    return bs58.decode(response.data.signature);
                }
                catch (error) {
                    throw new WalletSignTransactionError(error === null || error === void 0 ? void 0 : error.message, error);
                }
            }
            catch (error) {
                this.emit('error', error);
                throw error;
            }
        });
    }
}
//# sourceMappingURL=adapter.js.map